import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box, Checkbox, Group, Paper, Text } from '@mantine/core'
import { IconGripVertical, IconMinus } from '@tabler/icons-react'
import type { ReactNode } from 'react'

import type { FactField } from '@/services/api/api.types'

import type { HeaderField } from './types'

interface SortableFieldProps {
  id: string
  field: FactField
  onChange?: (field: FactField) => void
  dragHandleRef?: (element: HTMLElement | null) => void
  children?: ReactNode
  headerData?: HeaderField
}

export const SortableField = ({ id, field, onChange, dragHandleRef, children, headerData }: SortableFieldProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  }

  // Use external dragHandleRef if provided, otherwise use internal one
  const activatorRef = dragHandleRef || setActivatorNodeRef

  // Determine if this is a header with any disabled children
  const isHeaderWithDisabledChildren =
    field.factType === 'header' && headerData?.children && headerData.children.some(child => !child.enabled)

  return (
    <Paper ref={setNodeRef} style={style} p={0} withBorder>
      <Group justify="space-between" align="flex-start" p="md" wrap="nowrap">
        <Box
          ref={activatorRef}
          {...attributes}
          {...listeners}
          aria-label={`Reorder ${field.name}`}
          style={{ flexShrink: 0 }}>
          <IconGripVertical size={16} style={{ cursor: 'grab', marginRight: '8px' }} aria-hidden="true" />
        </Box>
        <Text className={field.factType === 'header' ? 'header-field' : ''} style={{ flex: 1 }}>
          {field.name}
        </Text>
        <Checkbox
          checked={field.enabled}
          onChange={() => {
            const updatedField = { ...field, enabled: !field.enabled }
            onChange?.(updatedField)
          }}
          icon={isHeaderWithDisabledChildren ? IconMinus : undefined}
          style={{ flexShrink: 0 }}
        />
      </Group>
      {children && (
        <Box pl="sm" pb="sm">
          {children}
        </Box>
      )}
      <style>{`
        .header-field {
          font-size: var(--mantine-font-size-lg);
          font-weight: 600;
        }
      `}</style>
    </Paper>
  )
}
